/**
 * API configuration for different environments
 */

const getApiBaseUrl = (): string => {
  // Use environment variable if set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // In development, use localhost backend
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000';
  }

  // In production, use api.helppet.ai
  return 'https://api.helppet.ai';
};

export const API_BASE_URL = getApiBaseUrl();

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: `${API_BASE_URL}/api/v1/auth/token`,
    SIGNUP: `${API_BASE_URL}/api/v1/auth/signup`,
    ME: `${API_BASE_URL}/api/v1/auth/me`,
  },
  RAG: {
    QUERY: `${API_BASE_URL}/api/v1/rag/query`,
  },
  PRACTICES: {
    LIST: `${API_BASE_URL}/api/v1/practices/`,
    CREATE: `${API_BASE_URL}/api/v1/practices/`,
    GET: (id: string) => `${API_BASE_URL}/api/v1/practices/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/v1/practices/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/v1/practices/${id}`,
  },
  PET_OWNERS: {
    LIST: `${API_BASE_URL}/api/v1/pet_owners/`,
    PAGINATED: `${API_BASE_URL}/api/v1/pet_owners/paginated`,
    STATS: `${API_BASE_URL}/api/v1/pet_owners/stats`,
    CREATE: `${API_BASE_URL}/api/v1/pet_owners/`,
    GET: (uuid: string) => `${API_BASE_URL}/api/v1/pet_owners/${uuid}`,
    UPDATE: (uuid: string) => `${API_BASE_URL}/api/v1/pet_owners/${uuid}`,
    DELETE: (uuid: string) => `${API_BASE_URL}/api/v1/pet_owners/${uuid}`,
    BULK_IMPORT: `${API_BASE_URL}/api/v1/pet_owners/bulk-import`,
  },
  ASSOCIATIONS: {
    CREATE: `${API_BASE_URL}/api/v1/associations/`,
    UPDATE: (associationId: string) => `${API_BASE_URL}/api/v1/associations/${associationId}`,
    BY_PRACTICE: (practiceId: string) => `${API_BASE_URL}/api/v1/associations/practice/${practiceId}`,
    BY_PET_OWNER: (petOwnerId: string) => `${API_BASE_URL}/api/v1/associations/pet-owner/${petOwnerId}`,
    APPROVE: (associationId: string) => `${API_BASE_URL}/api/v1/associations/${associationId}/approve`,
    REJECT: (associationId: string) => `${API_BASE_URL}/api/v1/associations/${associationId}/reject`,
    DELETE: (associationId: string) => `${API_BASE_URL}/api/v1/associations/${associationId}`,
  },
  PETS: {
    LIST: `${API_BASE_URL}/api/v1/pets/`,
    CREATE: `${API_BASE_URL}/api/v1/pets/`,
    GET: (uuid: string) => `${API_BASE_URL}/api/v1/pets/${uuid}`,
    UPDATE: (uuid: string) => `${API_BASE_URL}/api/v1/pets/${uuid}`,
    DELETE: (uuid: string) => `${API_BASE_URL}/api/v1/pets/${uuid}`,
    BY_OWNER: (ownerUuid: string) => `${API_BASE_URL}/api/v1/pets/owner/${ownerUuid}`,
    REACTIVATE: (uuid: string) => `${API_BASE_URL}/api/v1/pets/${uuid}/reactivate`,
    SCHEDULE: (petId: string) => `${API_BASE_URL}/api/v1/pets/${petId}/immunization-schedule`,
    AUDIT: (petId: string) => `${API_BASE_URL}/api/v1/pets/${petId}/audit`,
  },
  SCHEDULE: {
    RESCHEDULE: (scheduleId: string) => `${API_BASE_URL}/api/v1/immunization-schedule/${scheduleId}/reschedule`,
    CANCEL: (scheduleId: string) => `${API_BASE_URL}/api/v1/immunization-schedule/${scheduleId}/cancel`,
    DELETE: (scheduleId: string) => `${API_BASE_URL}/api/v1/immunization-schedule/${scheduleId}`,
    PRACTICE_OVERDUE: (practiceId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-schedule/overdue`,
    TEMPLATES: `${API_BASE_URL}/api/v1/immunization-templates`,
    APPLY_TEMPLATE: (petId: string) => `${API_BASE_URL}/api/v1/pets/${petId}/immunization-schedule/apply-template`,
  },
  MEDICAL_RECORDS: {
    BY_PET: (petUuid: string) => `${API_BASE_URL}/api/v1/medical-records/pet/${petUuid}`,
    GET: (uuid: string) => `${API_BASE_URL}/api/v1/medical-records/${uuid}`,
    CREATE: `${API_BASE_URL}/api/v1/medical-records/`,
    UPDATE: (uuid: string) => `${API_BASE_URL}/api/v1/medical-records/${uuid}`,
    DELETE: (uuid: string) => `${API_BASE_URL}/api/v1/medical-records/${uuid}`,
    TIMELINE: (petUuid: string) => `${API_BASE_URL}/api/v1/medical-records/pet/${petUuid}/timeline`,
    HISTORY: (petUuid: string) => `${API_BASE_URL}/api/v1/medical-records/pet/${petUuid}/history`,
    FOLLOW_UPS_DUE: `${API_BASE_URL}/api/v1/medical-records/follow-ups/due`,
  },
  VISIT_TRANSCRIPTS: {
    BY_PET: (petUuid: string) => `${API_BASE_URL}/api/v1/visit-transcripts/pet/${petUuid}`,
    BY_APPOINTMENT: (appointmentUuid: string, petUuid?: string) =>
      `${API_BASE_URL}/api/v1/visit-transcripts/appointments/${appointmentUuid}/visits${petUuid ? `?pet_id=${petUuid}` : ''}`,
    GET: (transcriptUuid: string) => `${API_BASE_URL}/api/v1/visit-transcripts/${transcriptUuid}`,
    CREATE: `${API_BASE_URL}/api/v1/visit-transcripts/`,
    UPDATE: (transcriptUuid: string) => `${API_BASE_URL}/api/v1/visit-transcripts/${transcriptUuid}`,
    DELETE: (transcriptUuid: string) => `${API_BASE_URL}/api/v1/visit-transcripts/${transcriptUuid}`,
    AUDIO_PLAYBACK: (visitId: string) => `${API_BASE_URL}/api/v1/visit-transcripts/audio/playback/${visitId}`,
    AUDIO_UPLOAD_INITIATE: `${API_BASE_URL}/api/v1/visit-transcripts/audio/upload/initiate`,
    AUDIO_UPLOAD_COMPLETE: (visitId: string) => `${API_BASE_URL}/api/v1/visit-transcripts/audio/upload/complete/${visitId}`,
    DEBUG: (visitId: string) => `${API_BASE_URL}/api/v1/visit-transcripts/debug/${visitId}`,
  },
  APPOINTMENTS: {
    LIST_BY_PRACTICE: (practiceUuid: string) => `${API_BASE_URL}/api/v1/appointments/practice/${practiceUuid}`,
    LIST_BY_PRACTICE_FILTERED: (practiceUuid: string, params?: { start_date?: string; end_date?: string; days_back?: number; days_forward?: number }) => {
      const queryParams = new URLSearchParams();
      if (params?.start_date) queryParams.append('start_date', params.start_date);
      if (params?.end_date) queryParams.append('end_date', params.end_date);
      if (params?.days_back !== undefined) queryParams.append('days_back', params.days_back.toString());
      if (params?.days_forward !== undefined) queryParams.append('days_forward', params.days_forward.toString());
      const queryString = queryParams.toString();
      return `${API_BASE_URL}/api/v1/appointments/practice/${practiceUuid}/filtered${queryString ? `?${queryString}` : ''}`;
    },
    LIST_BY_PET_OWNER: (ownerUuid: string) => `${API_BASE_URL}/api/v1/appointments/pet-owner/${ownerUuid}`,
    LIST_BY_VET: (vetUuid: string) => `${API_BASE_URL}/api/v1/appointments/vet/${vetUuid}`,
    GET: (appointmentUuid: string) => `${API_BASE_URL}/api/v1/appointments/${appointmentUuid}`,
    CREATE: `${API_BASE_URL}/api/v1/appointments`,
    UPDATE: (appointmentUuid: string) => `${API_BASE_URL}/api/v1/appointments/${appointmentUuid}`,
    CANCEL: (appointmentUuid: string) => `${API_BASE_URL}/api/v1/appointments/${appointmentUuid}`,
  },
  DASHBOARD: {
    VET_DASHBOARD: (vetUuid: string) => `${API_BASE_URL}/api/v1/dashboard/vet/${vetUuid}`,
    VET_TODAY: (vetUuid: string) => `${API_BASE_URL}/api/v1/dashboard/vet/${vetUuid}/today`,
    PENDING_VISITS: (vetUuid: string) => `${API_BASE_URL}/api/v1/visits/vet/${vetUuid}/pending`,
  },
  UPLOAD: {
    AUDIO: `${API_BASE_URL}/api/v1/upload/audio`,
    AUDIO_PRESIGNED_URL: (visitId: string) => `${API_BASE_URL}/api/v1/upload/audio/${visitId}/presigned-url`,
  },
  VET_CONTACT: {
    SUBMIT: `${API_BASE_URL}/api/v1/vets/contact`,
  },
  REMINDERS: {
    LIST: `${API_BASE_URL}/api/v1/reminders`,
    GET: (reminderId: string) => `${API_BASE_URL}/api/v1/reminders/${reminderId}`,
    CREATE: `${API_BASE_URL}/api/v1/reminders`,
    UPDATE: (reminderId: string) => `${API_BASE_URL}/api/v1/reminders/${reminderId}`,
    BY_PET: (petId: string) => `${API_BASE_URL}/api/v1/reminders/pets/${petId}/reminders`,
    BY_PET_LOGS: (petId: string) => `${API_BASE_URL}/api/v1/reminders/pets/${petId}/reminder-history`,
    UPCOMING: `${API_BASE_URL}/api/v1/reminders/due`,
    OVERDUE: `${API_BASE_URL}/api/v1/reminders/overdue`,
    COMPLETE: (reminderId: string) => `${API_BASE_URL}/api/v1/reminders/${reminderId}/complete`,
    CANCEL: (reminderId: string) => `${API_BASE_URL}/api/v1/reminders/${reminderId}`,
    DELETE_FORM_TRIGGER_INSTANCE: (instanceId: string) => `${API_BASE_URL}/api/v1/forms/triggers/instances/${instanceId}`,
    DELETE_IMMUNIZATION_REMINDER_INSTANCE: (instanceId: string) => `${API_BASE_URL}/api/v1/reminders/immunization-reminder-instances/${instanceId}`,
  },
  APPOINTMENT_REMINDER_SETTINGS: {
    LIST: (practiceId: string) => `${API_BASE_URL}/api/v1/appointment-reminder-settings?practice_id=${practiceId}`,
    CREATE: `${API_BASE_URL}/api/v1/appointment-reminder-settings`,
    UPDATE: (settingId: string) => `${API_BASE_URL}/api/v1/appointment-reminder-settings/${settingId}`,
    DELETE: (settingId: string) => `${API_BASE_URL}/api/v1/appointment-reminder-settings/${settingId}`,
    SEND: (reminderId: string) => `${API_BASE_URL}/api/v1/reminders/${reminderId}/send`,
  },
  NO_VISIT_REMINDER_SETTINGS: {
    LIST: (practiceId: string) => `${API_BASE_URL}/api/v1/no-visit-reminder-settings?practice_id=${practiceId}`,
    CREATE: `${API_BASE_URL}/api/v1/no-visit-reminder-settings`,
    UPDATE: (settingId: string) => `${API_BASE_URL}/api/v1/no-visit-reminder-settings/${settingId}`,
    DELETE: (settingId: string) => `${API_BASE_URL}/api/v1/no-visit-reminder-settings/${settingId}`,
  },
  APPOINTMENT_REMINDER_LOGS: {
    LIST: `${API_BASE_URL}/api/v1/appointment-reminder-logs`,
  },
  NO_VISIT_REMINDER_LOGS: {
    LIST: `${API_BASE_URL}/api/v1/no-visit-reminder-logs`,
  },
  REMINDER_TYPES: {
    LIST: `${API_BASE_URL}/api/v1/reminder-types`,
    GET: (typeId: string) => `${API_BASE_URL}/api/v1/reminder-types/${typeId}`,
    CREATE: `${API_BASE_URL}/api/v1/reminder-types`,
    UPDATE: (typeId: string) => `${API_BASE_URL}/api/v1/reminder-types/${typeId}`,
    DELETE: (typeId: string) => `${API_BASE_URL}/api/v1/reminder-types/${typeId}`,
  },
  PET_OWNER_CRM: {
    LIST: `${API_BASE_URL}/api/v1/pet_owners/crm`,
    GET: (ownerId: string) => `${API_BASE_URL}/api/v1/pet_owners/crm/${ownerId}`,
    UPDATE_STATUS: (ownerId: string) => `${API_BASE_URL}/api/v1/pet_owners/crm/${ownerId}/status`,
    REFRESH: `${API_BASE_URL}/api/v1/pet_owners/crm/refresh`,
    SETTINGS: `${API_BASE_URL}/api/v1/pet_owners/crm/settings`,
  },
  ENCOUNTERS: {
    BASE: `${API_BASE_URL}/api/v1/encounters`,
    BY_PET: (petUuid: string) => `${API_BASE_URL}/api/v1/pets/${petUuid}/encounters/`,
  },
  ENCOUNTER_ATTACHMENTS: {
    INITIATE_UPLOAD: `${API_BASE_URL}/api/v1/upload/encounter/initiate`,
    COMPLETE_UPLOAD: `${API_BASE_URL}/api/v1/upload/encounter/complete`,
    LIST: (encounterId: string) => `${API_BASE_URL}/api/v1/upload/encounter/${encounterId}`,
    DELETE: (encounterId: string, fileId: string) => `${API_BASE_URL}/api/v1/upload/encounter/${encounterId}/${fileId}`,
  },
  ENCOUNTER_CONSENT: {
    INITIATE_UPLOAD: `${API_BASE_URL}/api/v1/upload/consent/initiate`,
    COMPLETE_UPLOAD: `${API_BASE_URL}/api/v1/upload/consent/complete`,
    LIST: (encounterId: string) => `${API_BASE_URL}/api/v1/encounter/${encounterId}/consent-forms`,
    UPDATE_SIGNATURE_BLOCK: (fileId: string) => `${API_BASE_URL}/api/v1/consent/${fileId}/signature-block`,
    GENERATE_LINK: (fileId: string) => `${API_BASE_URL}/api/v1/consent/${fileId}/link`,
    GET_PUBLIC: (token: string) => `${API_BASE_URL}/api/v1/public/consent/${token}`,
    SIGN_PUBLIC: (token: string) => `${API_BASE_URL}/api/v1/public/consent/${token}/sign`,
    DELETE: (encounterId: string, fileId: string) => `${API_BASE_URL}/api/v1/upload/consent/${encounterId}/${fileId}`,
  },
  PET_PHOTOS: {
    INITIATE_UPLOAD: `${API_BASE_URL}/api/v1/upload/pet/initiate`,
    COMPLETE_UPLOAD: `${API_BASE_URL}/api/v1/upload/pet/complete`,
  },
  PET_OWNER_PHOTOS: {
    INITIATE_UPLOAD: `${API_BASE_URL}/api/v1/upload/pet-owner/initiate`,
    COMPLETE_UPLOAD: `${API_BASE_URL}/api/v1/upload/pet-owner/complete`,
  },
  CHAT_ATTACHMENTS: {
    INITIATE_UPLOAD: `${API_BASE_URL}/api/v1/chat/attachments/initiate`,
    COMPLETE_UPLOAD: `${API_BASE_URL}/api/v1/chat/attachments/complete`,
  },
  IMMUNIZATIONS: {
    BY_PET: (petUuid: string) => `${API_BASE_URL}/api/v1/pets/${petUuid}/immunizations`,
    CREATE: (petUuid: string) => `${API_BASE_URL}/api/v1/pets/${petUuid}/immunizations`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/v1/immunizations/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/v1/immunizations/${id}`,
  },
  IMMUNIZATION_CATALOG: {
    LIST: (practiceId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-catalog`,
    CREATE: (practiceId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-catalog`,
    UPDATE: (practiceId: string, itemId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-catalog/${itemId}`,
    DELETE: (practiceId: string, itemId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-catalog/${itemId}`,
    HARD_DELETE: (practiceId: string, itemId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/immunization-catalog/${itemId}/hard`,
  },
  MEDICATIONS: {
    BY_PET: (petUuid: string) => `${API_BASE_URL}/api/v1/pets/${petUuid}/medications`,
    CREATE: (petUuid: string) => `${API_BASE_URL}/api/v1/pets/${petUuid}/medications`,
    GET: (id: string) => `${API_BASE_URL}/api/v1/medications/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/v1/medications/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/v1/medications/${id}`,
    DISCONTINUE: (id: string) => `${API_BASE_URL}/api/v1/medications/${id}/discontinue`,
  },
  MEDICATION_CATALOG: {
    LIST: (practiceId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/medication-catalog`,
    CREATE: (practiceId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/medication-catalog`,
    UPDATE: (practiceId: string, itemId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/medication-catalog/${itemId}`,
    DELETE: (practiceId: string, itemId: string) => `${API_BASE_URL}/api/v1/practices/${practiceId}/medication-catalog/${itemId}`,
    SYNC: `${API_BASE_URL}/api/v1/medications/catalog/sync`,
    IMPORT_FROM_BILLING: `${API_BASE_URL}/api/v1/medications/catalog/import-from-billing`,
  },
  NOTIFICATION_AUDIT: {
    LIST: `${API_BASE_URL}/api/v1/notification-audit`,
    STATS: `${API_BASE_URL}/api/v1/notification-audit/stats`,
    AUDIENCES: `${API_BASE_URL}/api/v1/notification-audit/audiences`,
  },
  PET_AUDIT: {
    LIST: (petUuid: string, limit?: number, offset?: number) => {
      const params = new URLSearchParams();
      if (limit !== undefined) params.append('limit', limit.toString());
      if (offset !== undefined) params.append('offset', offset.toString());
      const queryString = params.toString();
      return `${API_BASE_URL}/api/v1/pets/${petUuid}/audit${queryString ? `?${queryString}` : ''}`;
    },
  },
  PET_OWNER_AUDIT: {
    LIST: (ownerUuid: string) => `${API_BASE_URL}/api/v1/pet_owners/${ownerUuid}/audit`,
    LIST_ALL: `${API_BASE_URL}/api/v1/pet_owner_audit`,
  },
  CLIENT_TAGS: {
    LIST: `${API_BASE_URL}/api/v1/settings/client-tags`,
    ADD: `${API_BASE_URL}/api/v1/settings/client-tags`,
    DELETE: (tagName: string) => `${API_BASE_URL}/api/v1/settings/client-tags/${encodeURIComponent(tagName)}`,
  }
} as const;
